import { createContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { createClient, Session, SupabaseClient } from '@supabase/supabase-js';
import { RegistrationFormData } from '../components/auth/RegistrationForm';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const apiUrl = import.meta.env.VITE_API_URL;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Missing Supabase environment variables');
}

const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  db: { schema: 'itsalt' },
});

export interface User {
  id: string;
  fullName: string;
  phone: string;
  isSuperadmin: boolean;
  telegramUsername?: string | null;
  authId?: string;
}

export interface VerifyOtpResult {
  needsRegistration: boolean;
  tempToken?: string;
  user?: User;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabase: SupabaseClient<any, 'itsalt'>;
  sendOtp: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, otp: string) => Promise<VerifyOtpResult>;
  register: (data: RegistrationFormData, tempToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Ref to track if user was already set by register/verifyOtp
  // This prevents handleAuthChange from overwriting the user with stale fetchUser result
  const userSetByAuthRef = useRef(false);

  // Fetch user data from customers table
  const fetchUser = useCallback(async (authId: string) => {
    try {
      const { data: customer, error } = await supabase
        .from('customers')
        .select('id, full_name, phone, is_superadmin, telegram_username')
        .eq('auth_id', authId)
        .single();

      if (error || !customer) {
        console.error('Failed to fetch user:', error);
        return null;
      }

      return {
        id: customer.id,
        fullName: customer.full_name,
        phone: customer.phone,
        isSuperadmin: customer.is_superadmin,
        telegramUsername: customer.telegram_username,
        authId,
      };
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  }, []);

  // Check existing session on mount
  useEffect(() => {
    let isMounted = true;
    let isInitialized = false;

    // Handle auth state changes
    const handleAuthChange = async (session: Session | null) => {
      if (!isMounted) return;

      console.log('handleAuthChange: session', session ? 'exists' : 'null');
      setSession(session);

      if (session?.user?.id) {
        // Skip fetchUser if user was already set by register/verifyOtp
        // This prevents race condition where handleAuthChange overwrites user with stale data
        if (userSetByAuthRef.current) {
          console.log('handleAuthChange: skipping fetchUser, user already set by auth flow');
          userSetByAuthRef.current = false; // Reset for future auth changes
          if (isMounted) {
            setLoading(false);
          }
          return;
        }

        console.log('handleAuthChange: fetching user for authId', session.user.id);
        const userData = await fetchUser(session.user.id);
        console.log('handleAuthChange: fetchUser result', userData);
        if (isMounted) {
          setUser(userData);
        }
      } else {
        setUser(null);
      }

      if (isMounted) {
        setLoading(false);
      }
    };

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event);

      // Use INITIAL_SESSION for initial load
      if (event === 'INITIAL_SESSION') {
        isInitialized = true;
        await handleAuthChange(session);
        return;
      }

      // Handle other events only after initialization
      if (isInitialized && isMounted) {
        await handleAuthChange(session);
      }
    });

    // Fallback: if INITIAL_SESSION doesn't fire within 3 seconds, try getSession
    const fallbackTimer = setTimeout(async () => {
      if (!isInitialized && isMounted) {
        console.log('Fallback: INITIAL_SESSION did not fire, using getSession');
        try {
          const { data: { session } } = await supabase.auth.getSession();
          isInitialized = true;
          await handleAuthChange(session);
        } catch (error) {
          console.error('Fallback getSession error:', error);
          if (isMounted) {
            setLoading(false);
          }
        }
      }
    }, 3000);

    return () => {
      isMounted = false;
      clearTimeout(fallbackTimer);
      subscription.unsubscribe();
    };
  }, [fetchUser]);

  // Send OTP to phone
  const sendOtp = async (phone: string) => {
    const response = await fetch(`${apiUrl}/auth-send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send OTP');
    }
  };

  // Verify OTP and get user status
  const verifyOtp = async (phone: string, otp: string): Promise<VerifyOtpResult> => {
    const response = await fetch(`${apiUrl}/auth-verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, otp }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Invalid OTP');
    }

    const data = await response.json();

    // For existing users: set user and session immediately
    if (!data.needsRegistration) {
      // Set user from backend response directly (don't wait for fetchUser)
      // This prevents race condition with onAuthStateChange
      if (data.user) {
        setUser(data.user);
        // Mark that user was set, so handleAuthChange won't overwrite it
        userSetByAuthRef.current = true;
      }

      // If session is returned, set it in Supabase client
      if (data.session) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        if (sessionError) {
          console.error('Failed to set session:', sessionError);
        } else {
          // Get session from Supabase client
          const { data: sessionData } = await supabase.auth.getSession();
          setSession(sessionData.session);
        }
      }

      // Explicitly set loading to false before returning
      // This ensures ProtectedRoute sees the user immediately
      setLoading(false);
    }

    return {
      needsRegistration: data.needsRegistration,
      tempToken: data.tempToken,
      user: data.user,
    };
  };

  // Complete registration
  const register = async (formData: RegistrationFormData, tempToken: string) => {
    const response = await fetch(`${apiUrl}/auth-register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tempToken}`,
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Registration failed');
    }

    const data = await response.json();

    // Set user from backend response directly (don't wait for fetchUser)
    // This prevents race condition with onAuthStateChange
    if (data.user) {
      setUser(data.user);
      // Mark that user was set, so handleAuthChange won't overwrite it
      userSetByAuthRef.current = true;
    }

    // If session is returned, set it in Supabase client
    if (data.session) {
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });

      if (sessionError) {
        console.error('Failed to set session:', sessionError);
      } else {
        // Get session from Supabase client
        const { data: sessionData } = await supabase.auth.getSession();
        setSession(sessionData.session);
      }
    }

    // Explicitly set loading to false before returning
    // This ensures ProtectedRoute sees the user immediately
    setLoading(false);
  };

  // Logout
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  // Refresh user data
  const refreshUser = async () => {
    if (session?.user?.id) {
      const userData = await fetchUser(session.user.id);
      setUser(userData);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        supabase,
        sendOtp,
        verifyOtp,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
